import { useCallback, useEffect, useRef, useState } from 'react';
import { createAudioRecorder } from './audioRecorder';
import { buildAcceptTypes, showAttach as ruleShowAttach, showAudio as ruleShowAudio, UPLOAD_FAILED_MESSAGE } from './media-rules';
import type { WidgetFeatures } from '../types';

export type SendMedia = (file: File) => Promise<void> | void;

export interface MediaComposer {
  fileInputRef: React.RefObject<HTMLInputElement>;
  acceptTypes: string;
  showAttach: boolean;
  showAudio: boolean;
  recording: boolean;
  recordingSeconds: number;
  uploading: boolean;
  openFilePicker: () => void;
  handleFileSelected: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  startRecording: () => Promise<void>;
  stopAndSendAudio: () => Promise<void>;
  cancelRecording: () => void;
  formatRecordingTime: (s: number) => string;
}

/**
 * Máquina de estados compartilhada do envio de mídia (arquivo + gravação de áudio).
 * Cada input (embed / hosted) desenha o próprio chrome e apenas consome estes controles.
 * `disabled` reflete socket desconectado — bloqueia abrir o file picker.
 */
export function useMediaComposer(
  onSendMedia: SendMedia | undefined,
  features: WidgetFeatures | undefined,
  disabled: boolean,
): MediaComposer {
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<ReturnType<typeof createAudioRecorder> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (recording) {
      intervalRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setRecordingSeconds(0);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [recording]);

  // Solta o microfone se o componente desmontar durante uma gravação.
  useEffect(() => {
    return () => {
      recorderRef.current?.cancel();
      recorderRef.current = null;
    };
  }, []);

  const openFilePicker = useCallback(() => {
    if (!onSendMedia || disabled) return;
    fileInputRef.current?.click();
  }, [onSendMedia, disabled]);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !onSendMedia) return;
      setUploading(true);
      try {
        await onSendMedia(file);
      } catch (err) {
        // Recusa do servidor (tipo não aceito, grande demais...) — antes o
        // visitante ficava sem resposta nenhuma.
        alert(err instanceof Error && err.message ? err.message : UPLOAD_FAILED_MESSAGE);
      } finally {
        setUploading(false);
        e.target.value = ''; // permite reselecionar o mesmo arquivo
      }
    },
    [onSendMedia],
  );

  const startRecording = useCallback(async () => {
    if (!onSendMedia) return;
    try {
      recorderRef.current = createAudioRecorder();
      await recorderRef.current.start();
      setRecording(true);
    } catch (err) {
      console.error('Audio recording failed:', err);
      alert('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  }, [onSendMedia]);

  const stopAndSendAudio = useCallback(async () => {
    if (!recorderRef.current || !onSendMedia) return;
    try {
      const file = await recorderRef.current.stop();
      setRecording(false);
      setUploading(true);
      await onSendMedia(file);
    } catch (err) {
      console.error('Audio stop failed:', err);
      setRecording(false);
    } finally {
      setUploading(false);
      recorderRef.current = null;
    }
  }, [onSendMedia]);

  const cancelRecording = useCallback(() => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const formatRecordingTime = useCallback((s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  }, []);

  return {
    fileInputRef,
    acceptTypes: buildAcceptTypes(features),
    showAttach: ruleShowAttach(features),
    showAudio: ruleShowAudio(features),
    recording,
    recordingSeconds,
    uploading,
    openFilePicker,
    handleFileSelected,
    startRecording,
    stopAndSendAudio,
    cancelRecording,
    formatRecordingTime,
  };
}
